import { ExportConfig, ExportableData } from './types';

export const exportToJSON = (data: ExportableData, options: ExportConfig): void => {
  const fileName = generateFileName(options);
  const formattedData = options.preserveTimestamp ? data : formatDatesInData(data);
  const jsonData = new Blob([JSON.stringify(formattedData, null, 2)], {
    type: 'application/json',
  });
  downloadFile(jsonData, `${fileName}.json`);
};

export const exportToCSV = (data: ExportableData[], options: ExportConfig): void => {
  const fileName = generateFileName(options);
  const csvContent = convertToCSV(data, options.preserveTimestamp || false);
  const csvData = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  downloadFile(csvData, `${fileName}.csv`);
};

const formatDatesInData = (data: unknown): unknown => {
  if (data === null || data === undefined) return data;

  if (data instanceof Date) {
    return data.toISOString().split('T')[0];
  }

  if (typeof data === 'string' && data.includes('T') && data.match(/^\d{4}-\d{2}-\d{2}T/)) {
    return data.split('T')[0];
  }

  if (Array.isArray(data)) {
    return data.map(formatDatesInData);
  }

  if (typeof data === 'object') {
    const formatted: Record<string, unknown> = {};
    for (const [key, value] of Object.entries(data)) {
      formatted[key] = formatDatesInData(value);
    }
    return formatted;
  }

  return data;
};

export const generateFileName = (options: ExportConfig): string => {
  if (!options.date) return options.fileName;

  const timestamp = new Date().toISOString().split('T')[0];
  return `${options.fileName}_${timestamp}`;
};

export const getPreviewFileName = (fileName: string, date: boolean, format: string): string => {
  const baseFileName = fileName || 'export';
  const dateSuffix = date ? `_${new Date().toISOString().split('T')[0]}` : '';
  return `${baseFileName}${dateSuffix}.${format}`;
};

const downloadFile = (blob: Blob, fileName: string): void => {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

const convertToCSV = (objArray: ExportableData[], preserveTimestamp: boolean = false): string => {
  if (objArray.length === 0) return '';

  const headers = [...new Set(objArray.flatMap(obj => Object.keys(obj)))];
  const csvHeaders = headers.join(',');

  const csvRows = objArray.map(obj => {
    return headers
      .map(header => {
        const value = obj[header];
        if (value === null || value === undefined) return '';

        let stringValue = String(value);

        if (!preserveTimestamp) {
          if (value instanceof Date) {
            stringValue = value.toISOString().split('T')[0];
          } else if (
            typeof value === 'string' &&
            value.includes('T') &&
            value.match(/^\d{4}-\d{2}-\d{2}T/)
          ) {
            stringValue = value.split('T')[0];
          }
        }

        if (stringValue.includes(',') || stringValue.includes('"') || stringValue.includes('\n')) {
          return `"${stringValue.replace(/"/g, '""')}"`;
        }
        return stringValue;
      })
      .join(',');
  });

  return [csvHeaders, ...csvRows].join('\r\n');
};
